import React, { createContext, useContext } from 'react';
import { Badge } from '../ui/Badge.js';

export interface DiffRowProps {
  label: string;
  current: React.ReactNode;
  proposed: React.ReactNode;
  unchanged?: boolean;
  field?: string;
  'data-testid'?: string;
  testId?: string;
}

export interface DiffTableProps {
  compact?: boolean;
  children: React.ReactNode;
  className?: string;
  'data-testid'?: string;
  testId?: string;
}

const DiffTableContext = createContext<{ compact?: boolean }>({ compact: false });

export const DiffRow: React.FC<DiffRowProps> = ({
  label,
  current,
  proposed,
  unchanged = false,
  field,
  'data-testid': dataTestId,
  testId
}) => {
  const { compact } = useContext(DiffTableContext);
  const testIdValue = dataTestId || testId || (field ? `diff-row-${field}` : undefined);

  if (compact) {
    return (
      <div data-testid={testIdValue} className="contents">
        <dt className="text-base-content/60 font-medium text-xs">{label}</dt>
        {unchanged ? (
          <dd className="text-base-content/60 italic text-xs">بدون تغییر (قبلاً اعمال شده)</dd>
        ) : (
          <dd className="flex items-center gap-1.5 min-w-0 text-xs">
            <span className="text-base-content/60 line-through decoration-error/60">{current}</span>
            <span className="text-base-content/40 text-xs shrink-0" aria-hidden="true">
              ←
            </span>
            <span className="text-success-soft font-semibold">{proposed}</span>
          </dd>
        )}
      </div>
    );
  }

  return (
    <tr
      data-testid={testIdValue}
      className="flex flex-col md:table-row py-3 md:py-0 border-b md:border-b-0 border-base-300 gap-1.5 md:gap-0"
    >
      <td className="w-full md:w-28 md:py-2.5 md:px-3 md:border-b md:border-base-300 align-top text-xs md:text-sm text-base-content/60 md:text-base-content/80 font-medium">
        {label}
      </td>
      {unchanged ? (
        <td
          colSpan={2}
          className="md:py-2.5 md:px-3 md:border-b md:border-base-300 align-top text-xs md:text-sm text-base-content/60 italic"
        >
          بدون تغییر (قبلاً اعمال شده)
        </td>
      ) : (
        <>
          <td className="md:py-2.5 md:px-3 md:border-b md:border-base-300 align-top text-xs md:text-sm text-base-content/60 flex items-center md:table-cell gap-2">
            <Badge tone="neutral" size="md" className="md:hidden">
              فعلی
            </Badge>
            <span>{current}</span>
          </td>
          <td className="md:py-2.5 md:px-3 md:border-b md:border-base-300 align-top text-xs md:text-sm flex items-center md:table-cell gap-2">
            <Badge tone="success" size="md" className="md:hidden">
              پیشنهادی
            </Badge>
            <span className="inline-block px-2 py-0.5 rounded-[10px] bg-success/12 text-success-soft font-semibold">
              {proposed}
            </span>
          </td>
        </>
      )}
    </tr>
  );
};

DiffRow.displayName = 'DiffRow';

export const DiffTable: React.FC<DiffTableProps> = ({
  compact = false,
  children,
  className = '',
  'data-testid': dataTestId,
  testId
}) => {
  const testIdValue = dataTestId || testId;

  return (
    <DiffTableContext.Provider value={{ compact }}>
      {compact ? (
        <dl
          data-testid={testIdValue}
          className={`grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs ${className}`}
        >
          {children}
        </dl>
      ) : (
        <div data-testid={testIdValue} className={`w-full overflow-x-auto ${className}`}>
          <table className="w-full text-sm border-separate border-spacing-0">
            <thead className="hidden md:table-header-group">
              <tr className="text-xs text-base-content/60 font-medium">
                <th className="w-28 py-2 px-3 text-start border-b border-base-300 font-medium">
                  مشخصه
                </th>
                <th className="py-2 px-3 text-start border-b border-base-300 font-medium">
                  فعلی
                </th>
                <th className="py-2 px-3 text-start border-b border-base-300 font-medium">
                  پیشنهادی
                </th>
              </tr>
            </thead>
            <tbody className="flex flex-col md:table-row-group divide-y divide-base-300 md:divide-y-0">
              {children}
            </tbody>
          </table>
        </div>
      )}
    </DiffTableContext.Provider>
  );
};

DiffTable.displayName = 'DiffTable';
